import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { API } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, RefreshCw, Trash2, FileText, ChevronRight, AlertCircle, IdCard } from "lucide-react";
import BuscaPorOab from "@/components/BuscaPorOab";
import { Link } from "react-router-dom";
import { formatarCnj } from "@/lib/masks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DADOS } from "../lib/larguras";

function formatNumero(n) {
  return formatarCnj(n) || "-";
}

// Sem isso, "Pendente sincronização" servia tanto para quem acabou de ser
// cadastrado quanto para quem o tribunal ainda não publicou — situações bem diferentes
// para o advogado.
// A situação é do processo, não do sincronizador.
//
// Antes o rótulo misturava as duas coisas — "Buscando dados", "Não publicado"
// — e o advogado não tinha como saber se o processo corre. Ele quer três
// respostas: corre, acabou, ou foi cancelado. O estado da busca é problema
// nosso e vai em outro lugar, discreto.
const ARQUIVADOS = ["Arquivado", "Baixado", "Arquivado definitivamente"];
const CANCELADOS = ["Cancelado", "Extinto", "Anulado"];

function situacaoTexto(processo) {
  const bruta = processo.situacao || "";
  if (ARQUIVADOS.includes(bruta)) return "Arquivado";
  if (CANCELADOS.includes(bruta)) return "Cancelado";
  return "Em andamento";
}

function corDaSituacao(processo) {
  const texto = situacaoTexto(processo);
  if (texto === "Arquivado") return "bg-[#F4F7FB] text-[#6685AD]";
  if (texto === "Cancelado") return "bg-amber-50 text-amber-800";
  return "bg-[#e5edf5] text-[#003882]";
}

// Aviso sobre a busca de dados — só quando há algo que o advogado precise
// saber. Processo normal, com movimentações chegando, não ganha aviso nenhum.
function avisoDaBusca(processo) {
  if (processo.sync_status === "nao_encontrado") {
    return "Este processo não está no DataJud, de onde vêm as movimentações. É comum em processos antigos.";
  }
  if (processo.sync_status === "erro") {
    return "A última consulta ao tribunal falhou. Tentamos de novo automaticamente.";
  }
  if (!processo.last_sync_at) {
    return "Ainda buscando as movimentações no tribunal.";
  }
  return "";
}

// O que está acontecendo com os processos, em uma faixa.
//
// Sem isto a tela mostrava dezenas de linhas "Aguardando 1ª sincronização" sem
// dizer o que isso significa nem quando termina — o advogado conclui que
// travou, e não tem como saber que há processos esperando decisão dele noutra
// tela.
function SituacaoDosProcessos({ situacao }) {
  if (!situacao) return null;

  const { pre_cadastro_pendentes: aguardandoDecisao, aguardando_sincronizacao: semCapa, ultima_importacao: ultima } = situacao;

  const importacaoEmCurso = ultima && ["na_fila", "processando"].includes(ultima.status);
  const importacaoFalhou = ultima && ultima.status === "erro";
  if (!aguardandoDecisao && !semCapa && !importacaoEmCurso && !importacaoFalhou) return null;

  return (
    <div className="flex flex-col gap-3 mb-6" data-testid="situacao-processos">
      {aguardandoDecisao > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[#003882]/25 bg-[#e5edf5] px-4 py-3">
          <p className="text-sm text-[#001533]">
            <strong>{aguardandoDecisao} processo(s)</strong> encontrado(s) pela busca por OAB
            esperando sua decisão. Não contam no limite do seu plano.
          </p>
          <Link to="/pre-cadastro"
                className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold text-sm rounded-md px-4 py-2 shrink-0">
            Revisar
          </Link>
        </div>
      )}

      {importacaoFalhou && (
        // Falha silenciosa fazia o advogado concluir que a OAB dele não tem
        // processos. O motivo técnico fica fora: ele não pode agir sobre um
        // RuntimeError, e ler isso só aumenta a sensação de que quebrou.
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-900">
            A consulta da OAB <strong>{ultima.numero}/{ultima.uf}</strong> não foi concluída —
            o PJe não respondeu. Seu cadastro está correto; tente de novo em alguns minutos.
          </p>
        </div>
      )}

      {importacaoEmCurso && (
        <div className="flex items-center gap-3 rounded-lg border border-[#D1DEEE] bg-white px-4 py-3">
          <RefreshCw size={16} className="animate-spin text-[#003882] shrink-0" aria-hidden="true" />
          <p className="text-sm text-[#335480]">
            Consultando o PJe para a OAB <strong>{ultima.numero}/{ultima.uf}</strong>.
            Avisamos por e-mail quando terminar — pode sair desta tela.
          </p>
        </div>
      )}

      {semCapa > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-[#D1DEEE] bg-white px-4 py-3">
          <AlertCircle size={16} className="text-[#6685AD] shrink-0" aria-hidden="true" />
          <p className="text-sm text-[#335480]">
            <strong>{semCapa}</strong> processo(s) aguardando a primeira busca de dados no
            tribunal. Classe, órgão e movimentações chegam automaticamente, em até algumas
            horas — você não precisa fazer nada.
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProcessosPanel() {
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newNumero, setNewNumero] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [buscaOab, setBuscaOab] = useState(false);
  // A ponte com o PJe pode estar desligada ou fora do ar. Oferecer o botão
  // nesse estado entrega ao advogado um clique que só produz um aviso de
  // indisponível — pior do que não oferecer.
  const [buscaOabDisponivel, setBuscaOabDisponivel] = useState(false);
  const [situacao, setSituacao] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [filtros, setFiltros] = useState({ tribunal: "", instancia: "", oab: "", parte: "", de: "", ate: "" });
  const [opcoes, setOpcoes] = useState({ tribunais: [], instancias: [], oabs: [] });
  const [painelAberto, setPainelAberto] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [paginacao, setPaginacao] = useState({ total: 0, paginas: 1 });

  const load = async (q = "", opcoes_ = {}) => {
    const filtroAtual = opcoes_.filtro ?? filtro;
    const paginaAtual = opcoes_.pagina ?? pagina;
    setLoading(true);
    try {
      const avancados = opcoes_.avancados ?? filtros;
      const params = { pagina: paginaAtual };
      if (q) params.search = q;
      if (filtroAtual !== "todos") params.situacao = filtroAtual;
      for (const [chave, valor] of Object.entries(avancados)) {
        if (valor) params[chave] = valor;
      }

      const res = await axios.get(`${API}/processos`, { params, withCredentials: true });
      // O endpoint devolvia um array; agora devolve página. Aceitar as duas
      // formas evita quebrar quem estiver com o bundle antigo em cache.
      const dados = Array.isArray(res.data)
        ? { processos: res.data, total: res.data.length, paginas: 1 }
        : res.data || {};
      setProcessos(dados.processos || []);
      setPaginacao({ total: dados.total || 0, paginas: dados.paginas || 1 });
    } catch {
      toast.error("Erro ao carregar processos");
    } finally {
      setLoading(false);
    }
  };

  // As opções vêm do que o escritório realmente tem: oferecer 27 tribunais a
  // quem atua em dois é filtro que atrapalha.
  useEffect(() => {
    axios.get(`${API}/processos/filtros`, { withCredentials: true })
      .then((res) => setOpcoes(res.data || {}))
      .catch(() => {});
  }, []);

  const aplicarAvancados = (novos) => {
    setFiltros(novos);
    setPagina(1);
    load(search, { pagina: 1, avancados: novos });
  };

  const limparAvancados = () => {
    const vazio = { tribunal: "", instancia: "", oab: "", parte: "", de: "", ate: "" };
    aplicarAvancados(vazio);
  };

  const avancadosAtivos = Object.values(filtros).filter(Boolean).length;

  const aplicarFiltro = (novo) => {
    setFiltro(novo);
    setPagina(1);
    load(search, { filtro: novo, pagina: 1 });
  };

  const irPara = (nova) => {
    setPagina(nova);
    load(search, { pagina: nova });
  };

  useEffect(() => {
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newNumero.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/processos`, {
        numero_cnj: newNumero.trim(),
        descricao: newDesc.trim() || null,
      }, { withCredentials: true });
      setProcessos((prev) => [res.data, ...prev]);
      setShowAdd(false);
      setNewNumero("");
      setNewDesc("");
      // Honestidade sobre o que acabou de acontecer: o cadastro terminou, a
      // consulta ao tribunal não. Dizer "sincronizado" aqui seria mentira.
      toast.success("Processo cadastrado. Buscando os dados no tribunal…");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar processo");
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/processos/recursos`, { withCredentials: true })
      .then((res) => {
        setBuscaOabDisponivel(!!res.data?.busca_por_oab?.disponivel);
        setSituacao(res.data?.situacao || null);
      })
      .catch(() => setBuscaOabDisponivel(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processos.length]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/processos/${deleteId}`, { withCredentials: true });
      setProcessos((prev) => prev.filter((p) => p.processo_id !== deleteId));
      toast.success("Processo removido");
    } catch {
      toast.error("Erro ao remover processo");
    } finally {
      setDeleteId(null);
    }
  };

  const handleSync = async (e, processoId) => {
    e.preventDefault();
    e.stopPropagation();
    setSyncing((prev) => ({ ...prev, [processoId]: true }));
    try {
      const res = await axios.post(`${API}/processos/${processoId}/sync`, {}, { withCredentials: true });
      if (res.data.synced_today) {
        toast.info("Processo já foi sincronizado hoje");
      } else {
        toast.success("Busca solicitada. Os dados chegam em alguns minutos.");
        setProcessos((prev) =>
          prev.map((p) => p.processo_id === processoId ? { ...p, ...res.data.processo } : p)
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncing((prev) => ({ ...prev, [processoId]: false }));
    }
  };

  return (
    <Layout>
      <div className={DADOS}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in-1">
          <div>
            <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">Jurídico</p>
            <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Processos
            </h1>
            <p className="text-[#335480] mt-1">
              Os processos que você acompanha. O que a busca por OAB encontrou e você
              ainda não decidiu fica em{" "}
              <Link to="/pre-cadastro" className="text-[#003882] underline font-medium">
                Fila de processos
              </Link>.
            </p>
          </div>
          <div className="flex gap-3">
            {buscaOabDisponivel && (
            <Button
              data-testid="busca-oab-btn"
              onClick={() => setBuscaOab(true)}
              variant="outline"
              className="border-[#D1DEEE] text-[#003882] font-semibold h-11 px-5"
            >
              <IdCard size={18} />
              Buscar pela OAB
            </Button>
            )}
            <Button
              data-testid="add-processo-btn"
              onClick={() => setShowAdd(true)}
              className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-11 px-6"
            >
              <Plus size={18} />
              Novo processo
            </Button>
          </div>
        </div>

        <SituacaoDosProcessos situacao={situacao} />

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-6 animate-fade-in-2">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#335480]" />
            <Input
              data-testid="search-processo-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número, classe, assunto..."
              className="pl-10 h-11 text-base border-[#D1DEEE]"
            />
          </div>
          <Button type="submit" variant="outline" className="border-[#D1DEEE] text-[#335480] h-11">
            Buscar
          </Button>
          {search && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { setSearch(""); load(""); }}
              className="border-[#D1DEEE] text-[#335480] h-11"
            >
              Limpar
            </Button>
          )}
        </form>

        {/* Os recortes respondem o que o advogado pergunta: o que ainda
            corre, o que acabou, e o que está sem movimentação — este último
            é onde mora tanto o processo parado quanto o que o DataJud não
            indexa. */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4" data-testid="filtros-processos">
          <div className="flex gap-1 bg-white border border-[#D1DEEE] rounded-lg p-1 flex-wrap">
            {[
              ["todos", "Todos"],
              ["em_andamento", "Em andamento"],
              ["arquivado", "Arquivados"],
              ["cancelado", "Cancelados"],
              ["sem_movimentacao", "Sem movimentação"],
            ].map(([valor, rotulo]) => (
              <button key={valor} type="button" onClick={() => aplicarFiltro(valor)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        filtro === valor ? "bg-[#003882] text-white" : "text-[#335480] hover:text-[#001533]"
                      }`}>
                {rotulo}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setPainelAberto((a) => !a)}
                    data-testid="abrir-filtros"
                    className="text-sm text-[#003882] font-medium underline">
              Filtros{avancadosAtivos > 0 ? ` (${avancadosAtivos})` : ""}
            </button>
            <p className="text-sm text-[#335480]">
              {paginacao.total} processo(s)
              {paginacao.paginas > 1 && ` · página ${pagina} de ${paginacao.paginas}`}
            </p>
          </div>
        </div>

        {painelAberto && (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-4 mb-4 grid gap-3 md:grid-cols-3"
               data-testid="painel-filtros">
            <div>
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">Tribunal</label>
              <select value={filtros.tribunal}
                      onChange={(e) => setFiltros({ ...filtros, tribunal: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-[#D1DEEE] bg-white px-3 text-sm text-[#001533]">
                <option value="">Todos</option>
                {(opcoes.tribunais || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">Instância</label>
              <select value={filtros.instancia}
                      onChange={(e) => setFiltros({ ...filtros, instancia: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-[#D1DEEE] bg-white px-3 text-sm text-[#001533]">
                <option value="">Todas</option>
                {(opcoes.instancias || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">OAB de origem</label>
              <select value={filtros.oab}
                      onChange={(e) => setFiltros({ ...filtros, oab: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-[#D1DEEE] bg-white px-3 text-sm text-[#001533]">
                <option value="">Todas</option>
                {(opcoes.oabs || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">
                Cliente ou advogado
              </label>
              <Input value={filtros.parte}
                     onChange={(e) => setFiltros({ ...filtros, parte: e.target.value })}
                     placeholder="Nome de uma parte ou do advogado que assina"
                     className="mt-1 h-10 border-[#D1DEEE]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">Distribuído de</label>
              <Input type="date" value={filtros.de}
                     onChange={(e) => setFiltros({ ...filtros, de: e.target.value })}
                     className="mt-1 h-10 border-[#D1DEEE]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#335480] uppercase tracking-wide">até</label>
              <Input type="date" value={filtros.ate}
                     onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })}
                     className="mt-1 h-10 border-[#D1DEEE]" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => aplicarAvancados(filtros)}
                      data-testid="aplicar-filtros"
                      className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold h-10 flex-1">
                Aplicar
              </Button>
              <Button onClick={limparAvancados} variant="outline"
                      className="border-[#D1DEEE] text-[#335480] h-10">
                Limpar
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-x-auto animate-fade-in-3">
          {loading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 bg-[#F4F7FB] rounded animate-pulse" />
              ))}
            </div>
          ) : processos.length === 0 ? (
            <div className="p-16 text-center">
              <FileText size={48} className="mx-auto mb-4 text-[#D1DEEE]" />
              <h3 className="text-xl font-semibold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
                Nenhum processo cadastrado
              </h3>
              <p className="text-[#335480] text-base mb-6">
                Adicione processos pelo número CNJ para começar.
              </p>
              <Button
                data-testid="add-processo-empty-btn"
                onClick={() => setShowAdd(true)}
                className="bg-[#003882] hover:bg-[#00285e] text-white"
              >
                <Plus size={16} />
                Cadastrar processo
              </Button>
            </div>
          ) : (
            <div>
              <div className="min-w-[960px]">
              <div className="grid grid-cols-12 gap-4 px-6 py-2.5 bg-[#F4F7FB] border-b border-[#D1DEEE] text-xs font-semibold text-[#335480] uppercase tracking-wide">
                <div className="col-span-3">Número CNJ</div>
                <div className="col-span-3">Classe/Assunto</div>
                <div className="col-span-2">Órgão</div>
                <div className="col-span-2 text-right">Valor da causa</div>
                <div className="col-span-1 text-center">Movs.</div>
                <div className="col-span-1 text-right">Ações</div>
              </div>
              {processos.map((p, i) => (
                <Link
                  to={`/processos/${p.processo_id}`}
                  key={p.processo_id}
                  data-testid={`processo-row-${i}`}
                  className="grid grid-cols-12 gap-4 px-6 py-3 items-center border-b border-[#D1DEEE] hover:bg-[#F4F7FB] transition-colors group"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="col-span-3 min-w-0">
                    <p className="text-[#001533] text-sm font-semibold tabular-nums">{formatNumero(p.numero_cnj)}</p>
                    {p.descricao && <p className="text-[#335480] text-sm truncate">{p.descricao}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${corDaSituacao(p)}`}>
                        {situacaoTexto(p)}
                      </span>
                      {avisoDaBusca(p) && (
                        <span className="text-[#6685AD] text-xs truncate" title={avisoDaBusca(p)}>
                          {avisoDaBusca(p)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <p className="text-[#001533] text-sm truncate" title={p.classe || ""}>{p.classe || "—"}</p>
                    <p className="text-[#335480] text-xs truncate" title={p.assunto || ""}>{p.assunto || "—"}</p>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-[#335480] text-sm truncate" title={p.orgao_julgador || ""}>{p.orgao_julgador || "—"}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-[#001533] text-sm tabular-nums">
                      {p.valor_causa ? `R$ ${p.valor_causa}` : "—"}
                    </p>
                  </div>
                  <div className="col-span-1 text-center">
                    <p className="text-[#001533] text-sm tabular-nums">
                      {p.movimentos_count ?? 0}
                    </p>
                  </div>
                  <div className="col-span-1 flex items-center gap-0.5 justify-end">
                    <button
                      data-testid={`sync-btn-${i}`}
                      onClick={(e) => handleSync(e, p.processo_id)}
                      className="p-1.5 rounded-md text-[#6685AD] hover:text-[#003882] hover:bg-[#e5edf5] transition-colors"
                      title="Sincronizar"
                    >
                      <RefreshCw size={15} className={syncing[p.processo_id] ? "animate-spin" : ""} />
                    </button>
                    <button
                      data-testid={`delete-btn-${i}`}
                      onClick={(e) => { e.preventDefault(); setDeleteId(p.processo_id); }}
                      className="p-1.5 rounded-md text-[#6685AD] hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Remover"
                    >
                      <Trash2 size={15} />
                    </button>
                    <ChevronRight size={16} className="text-[#D1DEEE] group-hover:text-[#003882] transition-colors" />
                  </div>
                </Link>
              ))}
              </div>
            </div>
          )}
        </div>

        {paginacao.paginas > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6" data-testid="paginacao">
            <Button variant="outline" disabled={pagina <= 1 || loading}
                    onClick={() => irPara(pagina - 1)}
                    className="border-[#D1DEEE] text-[#335480]">
              Anterior
            </Button>
            <span className="text-sm text-[#335480]">{pagina} de {paginacao.paginas}</span>
            <Button variant="outline" disabled={pagina >= paginacao.paginas || loading}
                    onClick={() => irPara(pagina + 1)}
                    className="border-[#D1DEEE] text-[#335480]">
              Próxima
            </Button>
          </div>
        )}
      </div>

      <BuscaPorOab open={buscaOab} onOpenChange={setBuscaOab} onImportado={load} />

      {/* Add Process Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Cadastrar Processo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label className="text-[#001533] font-medium">Número CNJ *</Label>
              <Input
                data-testid="new-processo-numero-input"
                value={newNumero}
                onChange={(e) => setNewNumero(e.target.value)}
                placeholder="0001234-56.2024.8.26.0001"
                required
                className="mt-1.5 h-11 text-base border-[#D1DEEE] font-mono"
              />
              <p className="text-[#335480] text-xs mt-1">Formato: NNNNNNN-DD.AAAA.J.TT.OOOO</p>
            </div>
            <div>
              <Label className="text-[#001533] font-medium">Descrição (opcional)</Label>
              <Input
                data-testid="new-processo-desc-input"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Ex: Ação de cobrança — Cliente Silva"
                className="mt-1.5 h-11 text-base border-[#D1DEEE]"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdd(false)}
                className="flex-1 border-[#D1DEEE]"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="confirm-add-processo-btn"
                disabled={adding}
                className="flex-1 bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
              >
                {adding ? "Cadastrando..." : "Cadastrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#001533]">Remover processo?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#335480]">
              Esta ação não pode ser desfeita. O processo e todas as suas anotações serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-processo-btn"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
